import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, UserRound, Code2, Briefcase } from "lucide-react";

const ROLES = [
  { id: "doctor", label: "Doctor", icon: UserRound, hint: "Clinicians building tools for patients." },
  { id: "developer", label: "Developer", icon: Code2, hint: "Engineers shipping healthcare apps." },
  { id: "admin", label: "Admin", icon: Briefcase, hint: "Ops & compliance leads." },
];

export default function AuthModal({ open, mode, onOpenChange }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(mode || "login");
  const [busy, setBusy] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({
    email: "", password: "", full_name: "", role: "developer", organization: ""
  });

  React.useEffect(() => { if (mode) setTab(mode); }, [mode, open]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back to ViewBox AI");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const submitReg = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(regForm);
      toast.success(`Welcome to ViewBox AI, ${regForm.full_name.split(" ")[0]}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Access ViewBox AI</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 text-xs">
            <Shield className="h-3 w-3 text-sky-600" />
            Encrypted in transit · No PHI stored in demo accounts.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={submitLogin} className="space-y-3 pt-2">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input id="login-email" type="email" required
                  data-testid="input-login-email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input id="login-password" type="password" required
                  data-testid="input-login-password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={busy}
                className="w-full bg-emerald-900 hover:bg-emerald-800 text-white"
                data-testid="btn-submit-login">
                {busy ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={submitReg} className="space-y-3 pt-2">
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  const active = regForm.role === r.id;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      onClick={() => setRegForm({ ...regForm, role: r.id })}
                      data-testid={`role-${r.id}`}
                      className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition-colors ${
                        active
                          ? "border-emerald-700 bg-emerald-50 text-emerald-900"
                          : "border-slate-200 hover:border-emerald-300 text-slate-600"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-semibold">{r.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                {ROLES.find((r) => r.id === regForm.role)?.hint}
              </p>

              <div>
                <Label htmlFor="reg-name">Full name</Label>
                <Input id="reg-name" required data-testid="input-reg-name"
                  value={regForm.full_name}
                  onChange={(e) => setRegForm({ ...regForm, full_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="reg-org">Organization</Label>
                <Input id="reg-org" placeholder="Optional" data-testid="input-reg-org"
                  value={regForm.organization}
                  onChange={(e) => setRegForm({ ...regForm, organization: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="reg-email">Email</Label>
                <Input id="reg-email" type="email" required data-testid="input-reg-email"
                  value={regForm.email}
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="reg-password">Password</Label>
                <Input id="reg-password" type="password" required minLength={6}
                  data-testid="input-reg-password"
                  value={regForm.password}
                  onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={busy}
                className="w-full bg-emerald-900 hover:bg-emerald-800 text-white"
                data-testid="btn-submit-register">
                {busy ? "Creating account..." : "Create account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
